import React, { useState, useEffect, useMemo, useRef } from 'react';
import { apiService } from '../../config/api';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import posthog from 'posthog-js'; // Added PostHog import

// Modern Mobile-First Menu Viewer Component
const MenuViewer = ({ vendorId }) => {
  // State for menu data
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [viewMode, setViewMode] = useState('images'); // Default or initial state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(null);

  // Refs for DOM elements
  const categoryNavRef = useRef(null);
  const sectionRefs = useRef({}); // To store refs for each category section div

  // State for category navigation bar scroll indicators
  const [showScrollIndicators, setShowScrollIndicators] = useState({ left: false, right: true });

  // Search and filtering state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    dietary: [],
    spiceLevel: [],
    priceRange: null,
    allergens: [],
    tags: []
  });
  const [filterOptions, setFilterOptions] = useState({
    dietary: [],
    spiceLevel: [],
    allergens: [],
    tags: [],
    priceRange: { min: 0, max: 1000 }
  });

  // Temporary state for filter drawer inputs
  const [tempMinPrice, setTempMinPrice] = useState(filterOptions.priceRange.min);
  const [tempMaxPrice, setTempMaxPrice] = useState(filterOptions.priceRange.max);


  // Memoized values for performance
  const hasStructuredData = useMemo(() => {
    return menu && menu.sections && menu.sections.length > 0;
  }, [menu]);

  const hasMenuImages = useMemo(() => {
    return menu && menu.sourceImageUrls && menu.sourceImageUrls.length > 0;
  }, [menu]);

  // Function to populate filter options from menu data
  const populateFilterOptions = (menuData) => {
    if (!menuData || (!menuData.sections && !menuData.categories)) return;
    const sections = menuData.sections || menuData.categories || [];
    const availableDietary = new Set();
    const availableSpiceLevels = new Set();
    const availableAllergens = new Set();
    const availableTags = new Set();
    const prices = [];

    const processItems = (items) => {
      if (!items) return;
      // Only process available items for filter options
      const availableItems = items.filter(item => item.available ?? true);
      availableItems.forEach(item => {
        (item.dietaryInfo || (item.attributes && item.attributes.dietary) || []).forEach(diet => availableDietary.add(diet));
        const spice = item.spiceLevel || (item.attributes && item.attributes.spiceLevel);
        if (spice) availableSpiceLevels.add(spice);
        (item.allergens || (item.attributes && item.attributes.allergens) || []).forEach(allergen => availableAllergens.add(allergen));
        if (item.tags) item.tags.forEach(tag => availableTags.add(tag));
        if (item.price) prices.push(item.price);
        if (item.variants) {
          const availableVariants = item.variants.filter(variant => variant.available ?? true);
          availableVariants.forEach(variant => { if (variant.price) prices.push(variant.price); });
        }
      });
    };

    sections.forEach(section => {
      processItems(section.items);
      if (section.subsections) section.subsections.forEach(subsection => processItems(subsection.items));
    });

    const cleanSet = (set) => Array.from(set).filter(item => item && item !== "null" && item !== "none" && item.toString().trim() !== "");
    let minPrice = 0, maxPrice = 1000;
    const validPrices = prices.filter(p => p !== null && p !== undefined);
    if (validPrices.length > 0) {
      minPrice = Math.floor(Math.min(...validPrices));
      maxPrice = Math.ceil(Math.max(...validPrices));
    }

    setFilterOptions({
      dietary: cleanSet(availableDietary),
      spiceLevel: cleanSet(availableSpiceLevels),
      allergens: cleanSet(availableAllergens),
      tags: cleanSet(availableTags),
      priceRange: { min: minPrice, max: maxPrice }
    });
    setTempMinPrice(minPrice);
    setTempMaxPrice(maxPrice);
  };

  // Effect to fetch menu data
  useEffect(() => {
    const fetchMenu = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.menus.getLatestMenu(vendorId);
        if (response.data && response.data.data) {
          const menuData = response.data.data;
          if (menuData.categories && !menuData.sections) {
            menuData.sections = menuData.categories.map(cat => ({ ...cat, id: cat.categoryId || cat.id, sectionId: cat.sectionId || cat.categoryId || cat.id }));
          } else if (menuData.sections) {
            menuData.sections = menuData.sections.map(sec => ({ ...sec, id: sec.sectionId || sec.categoryId || sec.id }));
          }
          setMenu(menuData);
          posthog.capture('public_menu_data_loaded', { vendorId, hasSections: menuData.sections && menuData.sections.length > 0, hasImages: menuData.sourceImageUrls && menuData.sourceImageUrls.length > 0 });

          if (menuData.sections && menuData.sections.length > 0) {
            setActiveCategoryId(menuData.sections[0].id);
          }
          populateFilterOptions(menuData);

          const currentHasStructuredData = menuData.sections && menuData.sections.length > 0;
          let initialViewMode = 'images';
          if (!currentHasStructuredData && menuData.sourceImageUrls && menuData.sourceImageUrls.length > 0) {
            setViewMode('images');
            initialViewMode = 'images';
          } else if (currentHasStructuredData) {
            setViewMode('structured');
            initialViewMode = 'structured';
          } else {
            setViewMode('images'); // Fallback
            initialViewMode = 'images_fallback_no_data';
          }
          posthog.capture('public_menu_view_initialized', { vendorId, initialViewMode, hasStructuredData: currentHasStructuredData, hasMenuImages: menuData.sourceImageUrls && menuData.sourceImageUrls.length > 0 });

        } else {
          setMenu(null);
          setViewMode('images'); // Fallback view
          posthog.capture('public_menu_no_data_in_response', { vendorId });
        }
      } catch (err) {
        console.error('Error fetching menu:', err);
        setError('Failed to load menu data. Please try again later.');
        posthog.capture('public_menu_load_error', { vendorId, error: err.message });
      } finally {
        setLoading(false);
      }
    };
    if (vendorId) {
      posthog.capture('public_menu_viewer_mounted', { vendorId });
      fetchMenu();
    }
  }, [vendorId]);

  // Effect to track viewMode changes
  useEffect(() => {
    // Avoid capturing on initial set, only subsequent changes by user/logic
    if (menu) { // Ensure menu is loaded before tracking view mode changes
        posthog.capture('public_menu_view_mode_changed', { vendorId, newViewMode: viewMode, hasStructuredData, hasMenuImages });
    }
  }, [viewMode, vendorId, menu, hasStructuredData, hasMenuImages]);


  // Effect for horizontal category navigation scroll indicators
  useEffect(() => {
    const checkScroll = () => {
      if (categoryNavRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = categoryNavRef.current;
        const threshold = 5;
        setShowScrollIndicators({
          left: scrollLeft > threshold,
          right: scrollLeft < scrollWidth - clientWidth - threshold
        });
      }
    };
    const navElement = categoryNavRef.current;
    if (navElement) {
      navElement.addEventListener('scroll', checkScroll, { passive: true });
      checkScroll();
      window.addEventListener('resize', checkScroll);
      return () => {
        navElement.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [menu, activeCategoryId]);

  // Effect for Intersection Observer to highlight category on page scroll
  useEffect(() => {
    if (!menu || !hasStructuredData) return;

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id.replace('category-', '');
          setActiveCategoryId(prevActiveId => {
            if (prevActiveId !== sectionId) {
              posthog.capture('public_menu_category_viewed_by_scroll', { vendorId, categoryId: sectionId });
            }
            return sectionId;
          });
        }
      });
    };

    // Options for the Intersection Observer
    // Adjust rootMargin top to account for sticky header height (e.g., 120px for header + 50px for nav = 170px)
    // The goal is to make a section "active" when its title is visible just below the sticky header/nav.
    const observerOptions = {
      root: null, // observing intersections relative to the viewport
      rootMargin: '-170px 0px -60% 0px', // (top, right, bottom, left) - bottom is negative to make it active sooner
      threshold: 0.01, // Trigger if even a small part is visible within the adjusted rootMargin
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const currentSectionRefs = sectionRefs.current;

    // Observe all section elements
    Object.values(currentSectionRefs).forEach(sectionEl => {
      if (sectionEl) observer.observe(sectionEl);
    });

    // Cleanup function to unobserve elements
    return () => {
      Object.values(currentSectionRefs).forEach(sectionEl => {
        if (sectionEl) observer.unobserve(sectionEl);
      });
    };
  }, [menu, hasStructuredData, vendorId]);

  // Effect to scroll the active category button into view
  useEffect(() => {
    if (activeCategoryId && categoryNavRef.current) {
      const button = categoryNavRef.current.querySelector(`button[data-section-id="${activeCategoryId}"]`);
      if (button) {
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeCategoryId]);


  const scrollNav = (direction) => {
    if (categoryNavRef.current) {
      const scrollAmount = direction === 'left' ? -categoryNavRef.current.clientWidth * 0.75 : categoryNavRef.current.clientWidth * 0.75;
      categoryNavRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      posthog.capture('public_menu_category_nav_scrolled', { vendorId, direction });
    }
  };

  const scrollToCategory = (sectionId) => {
    const sectionName = menu?.sections?.find(s => (s.id || s.sectionId || s.categoryId) === sectionId)?.name;
    setActiveCategoryId(sectionId);
    if (sectionRefs.current[sectionId]) {
      const headerHeight = 120;
      const yPosition = sectionRefs.current[sectionId].getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({ top: yPosition, behavior: 'smooth' });
      posthog.capture('public_menu_category_clicked_to_scroll', { vendorId, categoryId: sectionId, categoryName: sectionName || 'Unknown' });
    }
  };

  const toggleFilterDrawer = () => {
    if (!isFilterDrawerOpen) { // Drawer is opening
      posthog.capture('public_menu_filter_drawer_opened', { vendorId });
      if (filters.priceRange) {
        setTempMinPrice(filters.priceRange[0]);
        setTempMaxPrice(filters.priceRange[1]);
      } else {
        setTempMinPrice(filterOptions.priceRange.min);
        setTempMaxPrice(filterOptions.priceRange.max);
      }
    } else { // Drawer is closing (Apply button was clicked)
      let appliedPriceRange = null;
      if (tempMinPrice !== filterOptions.priceRange.min || tempMaxPrice !== filterOptions.priceRange.max || filters.priceRange !== null) {
        appliedPriceRange = [tempMinPrice, tempMaxPrice];
        setFilters(prev => ({ ...prev, priceRange: appliedPriceRange }));
      } else {
        setFilters(prev => ({ ...prev, priceRange: null }));
      }
      posthog.capture('public_menu_filters_applied_from_drawer', {
        vendorId,
        // The full 'filters' state isn't updated yet here for other filter types.
        // Individual toggles are captured by 'public_menu_filter_toggled'.
        // This event focuses on the "Apply" action from the drawer, mainly for price.
        appliedPriceRange: appliedPriceRange,
        currentOtherFilters: { // Snapshot of other filters at the moment of apply
            dietary: filters.dietary,
            spiceLevel: filters.spiceLevel,
            allergens: filters.allergens,
            tags: filters.tags,
        }
      });
    }
    setIsFilterDrawerOpen(!isFilterDrawerOpen);
  };

  const resetFilters = () => {
    const previousFilters = JSON.parse(JSON.stringify(filters)); // Deep copy
    const previousSearchTerm = searchTerm;
    setFilters({ dietary: [], spiceLevel: [], priceRange: null, allergens: [], tags: [] });
    setTempMinPrice(filterOptions.priceRange.min);
    setTempMaxPrice(filterOptions.priceRange.max);
    setSearchTerm('');
    posthog.capture('public_menu_filters_reset', { vendorId, previousFilters, previousSearchTerm });
  };

  const toggleFilter = (type, value) => {
    let isActiveAfterToggle;
    setFilters(prev => {
      const newFilters = { ...prev };
      if (type === 'priceRange') { // Should not be called for priceRange from here based on UI
        newFilters.priceRange = JSON.stringify(prev.priceRange) === JSON.stringify(value) ? null : value;
        isActiveAfterToggle = newFilters.priceRange !== null;
      } else {
        if (newFilters[type].includes(value)) {
          newFilters[type] = newFilters[type].filter(item => item !== value);
          isActiveAfterToggle = false;
        } else {
          newFilters[type] = [...newFilters[type], value];
          isActiveAfterToggle = true;
        }
      }
      return newFilters;
    });
    posthog.capture('public_menu_filter_toggled', {
        vendorId,
        filterType: type,
        filterValue: value,
        isActiveAfterToggle
      });
  };

  const filterMenuItems = (items) => {
    if (!items) return [];
    
    // Always filter for availability, even when no other filters are applied
    const availableItems = items.filter(item => item.available ?? true);
    
    if (searchTerm === '' && filters.dietary.length === 0 && filters.spiceLevel.length === 0 && filters.allergens.length === 0 && filters.tags.length === 0 && filters.priceRange === null) {
      return availableItems;
    }
    return availableItems.filter(item => {

      const matchesSearch = searchTerm === '' || item.name.toLowerCase().includes(searchTerm.toLowerCase()) || (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
      if (!matchesSearch) return false;
      const itemDietary = item.dietaryInfo || (item.attributes && item.attributes.dietary) || [];
      const dietaryMatch = filters.dietary.length === 0 || itemDietary.some(diet => filters.dietary.includes(diet));
      const itemSpiceLevel = item.spiceLevel || (item.attributes && item.attributes.spiceLevel) || null;
      const spiceLevelMatch = filters.spiceLevel.length === 0 || (itemSpiceLevel && filters.spiceLevel.includes(itemSpiceLevel));
      const itemAllergens = item.allergens || (item.attributes && item.attributes.allergens) || [];
      const allergenMatch = filters.allergens.length === 0 || !filters.allergens.some(allergen => itemAllergens.includes(allergen));
      const tagMatch = filters.tags.length === 0 || (item.tags && item.tags.some(tag => filters.tags.includes(tag)));
      let priceMatch = true;
      if (filters.priceRange) {
        const [min, max] = filters.priceRange;

        // Check base price first
        let itemMatches = item.price !== null && item.price !== undefined && item.price >= min && item.price <= max;

        // If base price doesn't match (or doesn't exist), check available variants
        if (!itemMatches && item.variants && item.variants.length > 0) {
          const availableVariants = item.variants.filter(variant => variant.available ?? true);
          itemMatches = availableVariants.some(variant =>
            variant.price !== null && variant.price !== undefined && variant.price >= min && variant.price <= max
          );
        }
        // If base price doesn't exist and there are no available variants, it doesn't match
        const availableVariants = (item.variants || []).filter(variant => variant.available ?? true);
        if ((item.price === null || item.price === undefined) && availableVariants.length === 0) {
          itemMatches = false;
        }

        priceMatch = itemMatches;
      }
      return dietaryMatch && spiceLevelMatch && allergenMatch && tagMatch && priceMatch;
    });
  };

  // Memoized filtered sections
  const filteredSections = useMemo(() => {
    if (!menu || (!menu.sections && !menu.categories)) return [];
    const sections = menu.sections || menu.categories || [];
    return sections.map(section => {
      const items = section.items || [];
      const filteredItems = filterMenuItems(items);
      const subsectionsWithFilteredItems = section.subsections ?
        section.subsections.map(subsection => ({
          ...subsection,
          filteredItems: filterMenuItems(subsection.items || [])
        })) : [];
      return {
        ...section,
        id: section.id || section.sectionId || section.categoryId,
        filteredItems,
        subsections: subsectionsWithFilteredItems,
        hasFilteredItems: filteredItems.length > 0 || subsectionsWithFilteredItems.some(sub => sub.filteredItems.length > 0)
      };
    });
  }, [menu, searchTerm, filters]); // filterMenuItems is stable if its dependencies are listed here or it's memoized

  const hasSearchResults = useMemo(() => {
    const activeFilterOrSearch = searchTerm !== '' || filters.dietary.length > 0 || filters.spiceLevel.length > 0 || filters.allergens.length > 0 || filters.tags.length > 0 || filters.priceRange !== null;
    if (!activeFilterOrSearch) return true; // No filters active, so all results are "search results"
    const foundItems = filteredSections.some(section => section.hasFilteredItems);

    // Capture when a search/filter operation yields no results
    if (activeFilterOrSearch && !foundItems) {
      posthog.capture('public_menu_no_search_results_found', {
        vendorId,
        searchTerm,
        activeFilters: filters
      });
    }
    return foundItems;
  }, [filteredSections, searchTerm, filters, vendorId]);


  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const backToTopBtn = document.getElementById('back-to-top-btn');
      if (backToTopBtn) {
        if (scrollPosition > 300) backToTopBtn.classList.remove('opacity-0', 'pointer-events-none');
        else backToTopBtn.classList.add('opacity-0', 'pointer-events-none');
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const hasActiveFilters = searchTerm !== '' || filters.dietary.length > 0 || filters.spiceLevel.length > 0 || filters.allergens.length > 0 || filters.tags.length > 0 || filters.priceRange !== null;

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-6 h-64">
      <div className="w-12 h-12 mb-4 border-4 border-t-transparent rounded-full animate-spin border-primary-500"></div>
      <p className="font-medium text-text-500">Loading menu...</p>
    </div>
  );

  if (error) {
    // This state is already captured by public_menu_load_error
    return (
      <div className="p-4 bg-red-100 border-l-4 border-red-500 rounded-md text-red-700">
        {/* ... error display ... */}
         <div className="flex">
            <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
            </div>
            <div className="ml-3"><p className="text-sm">{error}</p></div>
        </div>
      </div>
    );
  }

  if (!menu) {
    posthog.capture('public_menu_viewer_no_menu_message_displayed', { vendorId });
    return (
      <div className="p-6 rounded-lg text-center bg-background-100">
        {/* ... no menu display ... */}
        <svg className="mx-auto h-12 w-12 text-text-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
        <h3 className="mt-2 font-medium text-text-500">No menu available</h3>
        <p className="mt-1 text-text-500">Please check back later.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      {/* Sticky Header: Search and Category Navigation */}
      <div className="sticky top-0 z-30 bg-white shadow-md">
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-grow">
              {/* ... search input ... */}
              <input
                type="text"
                placeholder="Search menu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onBlur={() => { // Capture search when user finishes typing (leaves input)
                  if (searchTerm.trim() !== '') {
                    posthog.capture('public_menu_search_submitted', { vendorId, searchTerm: searchTerm.trim(), finalNumberOfResults: filteredSections.reduce((acc, s) => acc + s.filteredItems.length + s.subsections.reduce((sAcc, ss) => sAcc + ss.filteredItems.length, 0),0) });
                  }
                }}
                className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-text-500 placeholder-gray-400"
              />
              {searchTerm && (
                <button className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setSearchTerm('')}>
                  <svg className="h-5 w-5 text-gray-400 hover:text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                </button>
              )}
            </div>
            <button
              onClick={toggleFilterDrawer}
              className={`relative flex items-center justify-center p-2.5 rounded-lg transition-colors ${hasActiveFilters ? 'bg-primary-500 text-white' : 'bg-gray-200 text-text-500 hover:bg-gray-300'}`}
            >
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" /></svg>
              {hasActiveFilters && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold text-white bg-accent-500">
                  {filters.dietary.length + filters.spiceLevel.length + filters.allergens.length + filters.tags.length + (filters.priceRange ? 1 : 0)}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Horizontal Category Navigation */}
        {viewMode === 'structured' && menu.sections && menu.sections.length > 0 && (
          <div className="relative border-t border-b border-gray-200">
            {showScrollIndicators.left && (
              <button onClick={() => scrollNav('left')} className="absolute left-0 top-0 bottom-0 z-10 w-10 flex items-center justify-center bg-gradient-to-r from-white via-white to-transparent hover:bg-opacity-50" style={{ touchAction: 'manipulation' }}>
                <svg className="h-5 w-5 text-text-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
              </button>
            )}
            <div
              ref={categoryNavRef}
              className="py-3 px-4 flex overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory scroll-p-4" // scroll-p-4 for padding consideration with snap
            >
              {menu.sections.map((section) => {
                const reliableId = section.id || section.sectionId || section.categoryId;
                if (!reliableId) {
                  console.warn("Rendering category button: Section/Category found without a reliable ID:", section);
                  return null;
                }
                return (
                  <button
                    key={reliableId}
                    data-section-id={reliableId}
                    onClick={() => scrollToCategory(reliableId)}
                    className={`flex-shrink-0 px-4 py-1.5 mr-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors duration-150 snap-center ${activeCategoryId === reliableId ? 'bg-primary-500 text-white' : 'bg-gray-200 text-text-500 hover:bg-gray-300'}`}
                  >
                    {section.name}
                  </button>
                );
              })}
            </div>
            {showScrollIndicators.right && (
              <button onClick={() => scrollNav('right')} className="absolute right-0 top-0 bottom-0 z-10 w-10 flex items-center justify-center bg-gradient-to-l from-white via-white to-transparent hover:bg-opacity-50" style={{ touchAction: 'manipulation' }}>
                <svg className="h-5 w-5 text-text-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="px-4 py-6">
        {viewMode === 'structured' && (
          <>
            {hasActiveFilters && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-text-500">Active filters:</span>
                <button onClick={resetFilters} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 text-text-500 hover:bg-gray-300">
                  Clear all
                  <svg className="ml-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
              </div>
            )}
            {hasSearchResults ? (
              <div className="space-y-10">
                {filteredSections.map((section) => {
                  if (!section.hasFilteredItems && searchTerm !== '') return null;
                  const reliableId = section.id || section.sectionId || section.categoryId;
                  return (
                    // Assign ref to the section div for IntersectionObserver
                    <div key={reliableId} id={`category-${reliableId}`} ref={el => sectionRefs.current[reliableId] = el} className="scroll-mt-32"> {/* scroll-mt for scroll target offset */}
                      <div className="mb-4">
                        <h2 className="text-2xl font-bold text-text-500">{section.name}</h2>
                        {section.description && (<p className="mt-1 text-text-500">{section.description}</p>)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {section.filteredItems.map(item => (<ModernMenuItemCard key={item.itemId || item.id} item={item} vendorId={vendorId} sectionId={reliableId} />))}
                        {section.subsections && section.subsections.map(subsection =>
                          subsection.filteredItems.length > 0 && (
                            <React.Fragment key={subsection.subsectionId || subsection.id}>
                              {subsection.filteredItems.map(item => (
                                <ModernMenuItemCard key={item.itemId || item.id} item={item} vendorId={vendorId} sectionId={reliableId} subsectionId={subsection.subsectionId || subsection.id} />
                              ))}
                            </React.Fragment>
                          )
                        )}
                      </div>
                      {/* Display footer if available */}
                      {section.footer && (
                        <div className="mt-4 p-2 rounded-lg">
                          <h4 className="font-semibold text-text-500">{section.footer}</h4>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 px-4 rounded-lg text-center bg-background-50">
                <svg className="mx-auto h-12 w-12 text-text-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <h3 className="mt-4 text-lg font-medium text-text-500">No matching items</h3>
                <p className="mt-2 text-text-500">Try adjusting your search or filters.</p>
                <button onClick={resetFilters} className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                  Clear all filters
                </button>
              </div>
            )}
          </>
        )}
        {/* Image View: Display Menu Images */}
        {viewMode === 'images' && hasMenuImages && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {menu.sourceImageUrls.map((url, index) => (
              <div key={index} className="aspect-w-3 aspect-h-4 rounded-lg overflow-hidden shadow-md">
                <img
                  src={url}
                  alt={`Menu page ${index + 1}`}
                  className="object-cover w-full h-full"
                  onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x800/E2E8F0/4A5568?text=Image+Not+Found"; }}
                />
              </div>
            ))}
          </div>
        )}
        {/* Fallback: No content available */}
        {viewMode === 'images' && !hasMenuImages && !hasStructuredData && (
          <div className="py-12 px-4 rounded-lg text-center bg-background-50" onLoad={() => posthog.capture('public_menu_no_content_message_displayed', { vendorId })}>
            {/* ... no menu content display ... */}
             <svg className="mx-auto h-12 w-12 text-text-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" > <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" /> </svg>
            <h3 className="mt-4 text-lg font-medium text-text-500">No Menu Content</h3>
            <p className="mt-2 text-text-500">This vendor hasn't uploaded a menu yet. Please check back later.</p>
          </div>
        )}
      </div>

      {/* Filter Drawer Modal */}
      {/* ... filter drawer JSX ... (toggleFilter for checkboxes already captures events) */}
        <div className={`fixed inset-0 z-50 transition-opacity ${isFilterDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} aria-hidden={!isFilterDrawerOpen}>
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={() => {
            toggleFilterDrawer(); // This will trigger the close logic and event capture
        }}></div>
        <div className="fixed inset-x-0 bottom-0 transform transition-transform ease-in-out duration-300 sm:max-w-lg sm:mx-auto sm:rounded-t-xl overflow-hidden">
          <div className={`bg-white h-auto max-h-[85vh] overflow-y-auto transform transition-transform ease-in-out duration-300 ${isFilterDrawerOpen ? 'translate-y-0' : 'translate-y-full'}`}>
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-2 text-primary-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" /></svg>
                <h3 className="text-lg font-medium text-text-500">Filters</h3>
              </div>
              <button onClick={toggleFilterDrawer} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500">
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Drawer Content: Filters */}
            <div className="px-4 py-5 divide-y divide-gray-200">
              {/* Dietary Options */}
              {filterOptions.dietary && filterOptions.dietary.length > 0 && (
                <div className="py-4">
                  <h4 className="text-base font-medium mb-3 text-text-500">Dietary Options</h4>
                  <div className="space-y-2">
                    {filterOptions.dietary.map(option => (
                      <label key={option} className="flex items-center cursor-pointer">
                        <input type="checkbox" checked={filters.dietary.includes(option)} onChange={() => toggleFilter('dietary', option)} className="h-4 w-4 border-gray-300 rounded accent-primary-500" />
                        <span className="ml-2 capitalize text-text-500">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {/* Spice Level */}
              {filterOptions.spiceLevel && filterOptions.spiceLevel.length > 0 && (
                <div className="py-4">
                  <h4 className="text-base font-medium mb-3 text-text-500">Spice Level</h4>
                  <div className="space-y-2">
                    {filterOptions.spiceLevel.map(option => (
                      <label key={option} className="flex items-center cursor-pointer">
                        <input type="checkbox" checked={filters.spiceLevel.includes(option)} onChange={() => toggleFilter('spiceLevel', option)} className="h-4 w-4 border-gray-300 rounded accent-primary-500" />
                        <span className="ml-2 capitalize text-text-500">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {/* Allergens */}
              {filterOptions.allergens && filterOptions.allergens.length > 0 && (
                <div className="py-4">
                  <h4 className="text-base font-medium mb-3 text-text-500">Exclude Allergens</h4>
                  <div className="space-y-2">
                    {filterOptions.allergens.map(option => (
                      <label key={option} className="flex items-center cursor-pointer">
                        <input type="checkbox" checked={filters.allergens.includes(option)} onChange={() => toggleFilter('allergens', option)} className="h-4 w-4 border-gray-300 rounded accent-primary-500" />
                        <span className="ml-2 capitalize text-text-500">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {/* Tags */}
              {filterOptions.tags && filterOptions.tags.length > 0 && (
                <div className="py-4">
                  <h4 className="text-base font-medium mb-3 text-text-500">Tags</h4>
                  <div className="space-y-2">
                    {filterOptions.tags.map(option => (
                      <label key={option} className="flex items-center cursor-pointer">
                        <input type="checkbox" checked={filters.tags.includes(option)} onChange={() => toggleFilter('tags', option)} className="h-4 w-4 border-gray-300 rounded accent-primary-500" />
                        <span className="ml-2 capitalize text-text-500">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {/* Price Range */}
              {filterOptions.priceRange && (filterOptions.priceRange.min < filterOptions.priceRange.max) && (
                <div className="py-4">
                  <h4 className="text-base font-medium mb-2 text-text-500">Price Range</h4>
                  {/* Display Selected Range */}
                  <div className="text-center text-sm text-gray-700 mb-4">
                    ₹{tempMinPrice} - ₹{tempMaxPrice}
                  </div>

                  {/* rc-slider Range Component */}
                  <Slider // Use the main Slider component
                        range // Add the 'range' prop to enable dual handles
                        value={[tempMinPrice, tempMaxPrice]} // Current selected range
                        min={filterOptions.priceRange.min}
                        max={filterOptions.priceRange.max}
                        onChange={(value) => {
                            setTempMinPrice(value[0]);
                            setTempMaxPrice(value[1]);
                        }}
                        step={1}
                        allowCross={false}
                        pushable={1}
                  />

                  {/* Display Min/Max Labels below slider */}
                  <div className="flex justify-between text-xs text-gray-500 mt-2 px-1">
                    <span>₹{filterOptions.priceRange.min}</span>
                    <span>₹{filterOptions.priceRange.max}</span>
                  </div>
                  {/* Basic validation message example */}
                  {tempMinPrice > tempMaxPrice && (
                    <p className="text-xs text-red-500 mt-1">Minimum price cannot be greater than maximum price.</p>
                  )}
                </div>
              )}
            </div>
            {/* Drawer Footer */}
            <div className="px-4 py-3 bg-gray-50 flex justify-between sticky bottom-0 border-t border-gray-200">
              <button onClick={() => {
                  resetFilters(); // Event captured within resetFilters
              }} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-text-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                Reset All
              </button>
              <button onClick={() => {
                  toggleFilterDrawer(); // Event captured within toggleFilterDrawer for applying
              }} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Menu Item Card Component
const ModernMenuItemCard = ({ item, vendorId, sectionId, subsectionId }) => {
  const [showVariants, setShowVariants] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Filter out unavailable variants (default to available if not set)
  const availableVariants = (item.variants || []).filter(variant => variant.available ?? true);

  const shouldDisplaySpiceLevel = () => {
    const spiceLevel = item.spiceLevel || (item.attributes && item.attributes.spiceLevel);
    return spiceLevel && spiceLevel !== "null" && spiceLevel !== "none" && spiceLevel.toString().trim() !== "";
  };
  
  const getSpiceLevelBadge = () => {
    const spiceLevelValue = item.spiceLevel || (item.attributes && item.attributes.spiceLevel);
    if (!shouldDisplaySpiceLevel() || !spiceLevelValue) return null;
    const spiceLevelText = spiceLevelValue.toLowerCase();
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-accent-500/[.2] text-accent-500 capitalize`}>
        {spiceLevelText}
      </span>
    );
  };

  const getDietaryBadges = () => {
    const dietaryInfo = item.dietaryInfo || (item.attributes && item.attributes.dietary) || [];
    if (dietaryInfo.length === 0) return null;
    return dietaryInfo.map(diet => (
      <span key={diet} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary-500/[.125] text-primary-500 capitalize">
        {diet}
      </span>
    ));
  };

  return (
    <div className="relative bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden flex flex-col">
      <div className="p-4 flex-grow">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-grow">
            <h3 className="text-lg font-semibold text-text-500">{item.name}</h3>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {getDietaryBadges()}
              {getSpiceLevelBadge()}
            </div>
          </div>
          <div className="flex-shrink-0 px-3 py-1 rounded-full font-semibold text-sm border bg-primary-500/[.05] text-text-500 border-primary-500/[.1]">
            {item.price > 0 ? (
              <>
                {item.currencySymbol || item.currency || '₹'} {item.price?.toFixed(2)}
              </>
            ) : null}
          </div>
        </div>
        {item.description && item.description !== 'null' && (
          <p className="text-sm text-text-500 mb-2">{item.description}</p>
        )}
      </div>

      {/* Expandable section for more details and variants */}
      <div className="px-4 pb-4">
        {((item.tags?.length > 0 ||
          ((item.allergens && item.allergens.length > 0) || (item.attributes && item.attributes.allergens && item.attributes.allergens.length > 0)) ||
          item.prepTime)) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs flex items-center text-primary-500 hover:text-primary-600 mb-2 w-full justify-start"
              aria-expanded={expanded}
            >
              {expanded ? 'Show less' : 'Show more details'}
              <svg className={`w-3 h-3 ml-1 transform transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        {expanded && (
          <div className="text-xs space-y-1 text-text-500 mb-3">
            {item.tags && item.tags.length > 0 && <p><strong>Tags:</strong> {item.tags.join(', ')}</p>}
            {(item.allergens && item.allergens.length > 0) && <p><strong>Allergens:</strong> {item.allergens.join(', ')}</p>}
            {(item.attributes && item.attributes.allergens && item.attributes.allergens.length > 0) && <p><strong>Allergens:</strong> {item.attributes.allergens.join(', ')}</p>}
            {item.prepTime && <p><strong>Prep time:</strong> {item.prepTime}</p>}
          </div>
        )}

        {availableVariants.length > 0 && (
          <div className="mt-auto"> {/* Pushes variants to the bottom if card height varies */}
            <button
              onClick={() => setShowVariants(!showVariants)}
              className="flex items-center justify-between w-full py-2 px-3 text-sm font-medium rounded-lg transition-colors text-primary-500 bg-primary-500/[.1] hover:bg-primary-500/[.15]"
              aria-expanded={showVariants}
            >
              <span>Available Options ({availableVariants.length})</span>
              <svg className={`w-4 h-4 transform transition-transform duration-150 ${showVariants ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showVariants && (
              <div className={`mt-2 overflow-hidden transition-all duration-300 ease-in-out ${showVariants ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  {availableVariants.map((variant, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <div className="flex items-center">
                        {variant.defaultOption && (
                          <span className="mr-2 text-xs px-1.5 py-0.5 rounded bg-accent-500/[.2] text-accent-500">
                            Default
                          </span>
                        )}
                        <span className={`${variant.defaultOption ? 'font-medium' : ''} text-text-500`}>{variant.name}</span>
                      </div>
                      <span className="font-medium text-text-500">{item.currencySymbol || item.currency || '₹'} {variant.price?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuViewer;